import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredDropdown } from '@/shared/hooks/useAnchoredDropdown';
import { useDateFormat } from '@/shared/utils/date';
import { moveFocusVertical, moveFocusHorizontal } from '@/shared/keynav/spatialNav';
import { IconCalendar, IconChevron, IconAlertCircle } from './Icon';
import { useField, fieldAria } from './FieldContext';

// A clean, on-brand calendar popover that replaces the (unstyleable) native
// <input type="date"> picker. Value is ISO YYYY-MM-DD; the trigger shows the
// date in the company's configured format. Keyboard: arrows move a day,
// PageUp/Down a month, Enter picks, Esc closes.

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number) => String(n).padStart(2, '0');
const toYmd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function parseYmd(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
function addMonths(d: Date, n: number): Date {
  const t = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  t.setDate(Math.min(d.getDate(), last));
  return t;
}

// Quick-jump presets for the calendar footer. Single-date semantics: period
// options land on the period's natural anchor (start of current periods, end
// of previous ones — the dates people actually mean when they say them).
const QUICK_JUMPS: Array<{ label: string; date: (today: Date) => Date }> = [
  { label: 'Yesterday', date: (t) => addDays(t, -1) },
  { label: 'Tomorrow', date: (t) => addDays(t, 1) },
  { label: 'Start of week', date: (t) => addDays(t, -t.getDay()) },
  { label: 'Start of month', date: (t) => new Date(t.getFullYear(), t.getMonth(), 1) },
  { label: 'End of month', date: (t) => new Date(t.getFullYear(), t.getMonth() + 1, 0) },
  { label: 'End of prev month', date: (t) => new Date(t.getFullYear(), t.getMonth(), 0) },
  { label: 'Start of prev month', date: (t) => new Date(t.getFullYear(), t.getMonth() - 1, 1) },
  { label: 'Start of quarter', date: (t) => new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1) },
  { label: 'End of prev quarter', date: (t) => new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 0) },
  { label: 'Start of year', date: (t) => new Date(t.getFullYear(), 0, 1) },
  { label: 'End of year', date: (t) => new Date(t.getFullYear(), 11, 31) },
  { label: 'End of prev year', date: (t) => new Date(t.getFullYear() - 1, 11, 31) },
  { label: 'Start of prev year', date: (t) => new Date(t.getFullYear() - 1, 0, 1) },
  { label: 'In 30 days', date: (t) => addDays(t, 30) },
];

function Calendar({ value, onPick, onClear, onClose }: {
  value: string | null; onPick: (ymd: string) => void; onClear: () => void; onClose: () => void;
}) {
  const selected = parseYmd(value);
  const today = new Date();
  // A single focused day is the source of truth for the visible month.
  const [focus, setFocus] = useState<Date>(() => selected || today);
  const [picking, setPicking] = useState<'day' | 'month'>('day');
  const [jumpsOpen, setJumpsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { rootRef.current?.focus(); }, []);

  const year = focus.getFullYear();
  const month = focus.getMonth();
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const start = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [year, month]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Peel one layer at a time: quick-dates overlay first, then the picker.
      if (jumpsOpen) { setJumpsOpen(false); return; }
      onClose();
      return;
    }
    if (picking !== 'day') return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); setFocus((f) => addDays(f, -1)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setFocus((f) => addDays(f, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocus((f) => addDays(f, -7)); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setFocus((f) => addDays(f, 7)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setFocus((f) => addMonths(f, -1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setFocus((f) => addMonths(f, 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); onPick(toYmd(focus)); }
  }

  const navBtn = 'h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-slate-100 hover:text-primary-text transition-colors';

  return (
    <div ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown} className="relative w-[252px] p-2.5 outline-none">
      <div className="flex items-center justify-between mb-1.5">
        <button type="button" aria-label="Previous"
          onClick={() => setFocus((f) => picking === 'day' ? addMonths(f, -1) : new Date(f.getFullYear() - 1, f.getMonth(), 1))}
          className={navBtn}><span className="inline-flex rotate-90"><IconChevron size={16} /></span></button>
        <button type="button" onClick={() => setPicking((p) => p === 'day' ? 'month' : 'day')}
          className="px-2.5 h-7 rounded-md text-[0.8125rem] font-semibold text-primary-text hover:bg-slate-100 transition-colors">
          {picking === 'day' ? `${MONTHS[month]} ${year}` : year}
        </button>
        <button type="button" aria-label="Next"
          onClick={() => setFocus((f) => picking === 'day' ? addMonths(f, 1) : new Date(f.getFullYear() + 1, f.getMonth(), 1))}
          className={navBtn}><span className="inline-flex -rotate-90"><IconChevron size={16} /></span></button>
      </div>

      {picking === 'day' ? (
        <>
          <div className="grid grid-cols-7">
            {WEEKDAYS.map((w) => <div key={w} className="text-center text-[0.65625rem] font-semibold uppercase tracking-wide text-muted/60 pb-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((d, i) => {
              const inMonth = d.getMonth() === month;
              const isToday = sameDay(d, today);
              const isSel = selected != null && sameDay(d, selected);
              const isFocus = sameDay(d, focus);
              return (
                <button key={i} type="button" tabIndex={-1}
                  onClick={() => onPick(toYmd(d))}
                  // Focus drives the visible month, so hover-focusing an
                  // adjacent-month cell would flip the calendar under the
                  // cursor — only refocus within the current month.
                  onMouseEnter={() => { if (inMonth) setFocus(d); }}
                  className={`h-[30px] w-[30px] mx-auto inline-flex items-center justify-center rounded-full text-[0.78125rem] font-numeric transition-colors
                    ${isSel ? 'bg-primary text-white font-semibold shadow-sm'
                      : isToday ? 'text-primary font-bold'
                      : inMonth ? 'text-primary-text' : 'text-muted/40'}
                    ${!isSel ? 'hover:bg-slate-100' : ''}
                    ${isFocus && !isSel ? 'ring-2 ring-inset ring-primary/40' : ''}`}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-3 gap-1 py-1">
          {MON_ABBR.map((m, i) => {
            const isSel = selected != null && selected.getFullYear() === year && selected.getMonth() === i;
            return (
              <button key={m} type="button"
                onClick={() => { setFocus(new Date(year, i, Math.min(focus.getDate(), new Date(year, i + 1, 0).getDate()))); setPicking('day'); }}
                className={`h-9 rounded-md text-[0.78125rem] font-medium transition-colors ${isSel ? 'bg-primary text-white' : month === i ? 'bg-primary-light text-primary' : 'text-primary-text hover:bg-slate-100'}`}>
                {m}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border-light relative">
        <button type="button" onClick={onClear} className="text-[0.75rem] text-muted hover:text-rose-600 px-1.5 py-1 rounded-md hover:bg-slate-100 transition-colors">Clear</button>
        {/* Quick jumps: sits between Clear and Today. Each option picks a
            concrete date (this is a single-date control). The list opens as a
            full-size overlay covering the calendar, so every row fits on one
            line instead of cramming into a mini dropdown. */}
        <button type="button" onClick={() => setJumpsOpen((o) => !o)}
          aria-expanded={jumpsOpen}
          className="text-[0.75rem] text-secondary px-1.5 py-1 rounded-md hover:bg-slate-100 transition-colors inline-flex items-center gap-0.5">
          More
          <svg width="9" height="9" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M1 1l4 4 4-4" /></svg>
        </button>
        <button type="button" onClick={() => onPick(toYmd(today))} className="text-[0.75rem] font-medium text-primary px-2 py-1 rounded-md hover:bg-primary-light transition-colors">Today</button>
      </div>

      {jumpsOpen && (
        <div className="absolute inset-0 z-30 rounded-xl bg-white p-2.5 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[0.78125rem] font-semibold text-primary-text">Quick dates</span>
            <button type="button" aria-label="Back to calendar" onClick={() => setJumpsOpen(false)}
              className="h-6 w-6 inline-flex items-center justify-center rounded-md text-muted hover:bg-slate-100 hover:text-primary-text transition-colors">×</button>
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {QUICK_JUMPS.map((j) => (
              <button key={j.label} type="button"
                onClick={() => onPick(toYmd(j.date(today)))}
                className="w-full text-left px-2 py-[7px] rounded-md text-[0.78125rem] hover:bg-primary-light/60 transition-colors flex items-center justify-between gap-2 whitespace-nowrap">
                <span>{j.label}</span>
                <span className="text-[0.6875rem] text-muted font-numeric">{MON_ABBR[j.date(today).getMonth()]} {j.date(today).getDate()}, {j.date(today).getFullYear()}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function DatePicker({
  value, onChange, disabled, readOnly, placeholder = 'Select date…',
  className = '', triggerClassName, ariaLabel, id, invalid,
}: {
  value: string | null;
  onChange: (v: string) => void;   // ISO YYYY-MM-DD, or '' when cleared
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;              // wrapper
  triggerClassName?: string;       // trigger button (defaults to a standard field box)
  ariaLabel?: string;
  id?: string;
  invalid?: boolean;          // red border + error icon + aria-invalid
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const inert = disabled || readOnly;
  const menuStyle = useAnchoredDropdown(rootRef, open && !inert, { minWidth: 252, maxHeight: 380 });
  const fmt = useDateFormat();
  // Inside a field the label names the date; a bare picker keeps "Date".
  const field = useField();
  const token = useId();
  const aria = fieldAria(field, { id, 'aria-label': ariaLabel }, invalid, token);
  const isInvalid = aria['aria-invalid'] === true || aria['aria-invalid'] === 'true';
  const name = ariaLabel ?? (field ? undefined : 'Date');

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  function commit(v: string) { onChange(v); setOpen(false); setTimeout(() => btnRef.current?.focus(), 0); }
  function close() { setOpen(false); setTimeout(() => btnRef.current?.focus(), 0); }

  const display = value ? fmt(value) : '';
  const defaultTrigger = 'w-full bg-white h-[26px] px-2 rounded-md border border-controlBorder outline-none transition-colors';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        data-travel=""
        id={aria.id}
        aria-label={name}
        aria-labelledby={aria['aria-labelledby']}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        aria-keyshortcuts={value && !inert ? 'Delete' : undefined}
        onClick={() => !inert && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (open) return;
          // Keyboard-accessible Clear (the inline × is a mouse-only target).
          if ((e.key === 'Delete' || e.key === 'Backspace') && value && !inert) { e.preventDefault(); onChange(''); return; }
          if (!e.ctrlKey && !e.metaKey && (e.key === 'Enter' || e.key === ' ' || e.key === 'F2' || e.key === 'F4'
            || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')))) {
            if (!inert) { e.preventDefault(); setOpen(true); }
            return;
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            moveFocusHorizontal(e.currentTarget, e.key === 'ArrowLeft' ? 'left' : 'right');
            return;
          }
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (!moveFocusVertical(e.currentTarget, e.key === 'ArrowUp' ? 'up' : 'down') && !inert) setOpen(true);
          }
        }}
        className={`group text-[0.8125rem] text-left inline-flex items-center gap-2 ${triggerClassName || defaultTrigger} ${inert ? 'cursor-default text-muted' : 'outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'} ${open ? 'border-primary ring-2 ring-inset ring-primary/40' : ''} ${isInvalid ? '!border-red-500' : ''}`}
        title={display || placeholder}
        data-empty={value ? undefined : ''}
      >
        <span className={`flex-1 min-w-0 truncate font-numeric ${display ? 'text-primary-text' : 'text-muted'}`}>{display || placeholder}</span>
        {value && !inert && <span aria-hidden="true" title="Clear (Delete)" onClick={(e) => { e.stopPropagation(); onChange(''); }} className="shrink-0 inline-flex h-[24px] w-[24px] items-center justify-center rounded text-muted hover:text-red-600 hover:bg-slate-100 cursor-pointer">×</span>}
        {isInvalid && <IconAlertCircle size={14} aria-hidden="true" className="shrink-0 text-red-600" />}
        <span className={`shrink-0 transition-colors ${open ? 'text-primary' : 'text-muted group-hover:text-secondary'}`}><IconCalendar size={14} /></span>
      </button>

      {open && menuStyle && createPortal(
        <div ref={panelRef} style={menuStyle} className="z-popover bg-white rounded-xl border border-border shadow-xl">
          <Calendar value={value} onPick={commit} onClear={() => commit('')} onClose={close} />
        </div>,
        document.documentElement,
      )}
    </div>
  );
}
